import React, { ReactNode, useEffect } from 'react'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { InferType } from 'yup'
import { UseMutateFunction } from '@tanstack/react-query'
import { useAppTranslation } from '../../hooks/useAppTranslation'
import { useToastNotifications } from '../../hooks/useToastNotification'
import { ImageActions } from '../../actions/image-upload'
import { ToastType } from '../common/Toast/toast-type'
import { MessageBarUpload } from '../chat/ChatMessageBar/MessageBarUpload'
import { MessageBarImages } from '../chat/ChatMessageBar/MessageBarImages'
import { TextArea } from '../common/TextArea'
import { Button, ButtonVariant } from '../common/Button'
import { IconChoices } from '../common/Icon'
import { MessageBarUploadDocuments } from '../chat/ChatMessageBar/MessageBarUploadDocuments'
import { DocumentActions } from '../../actions/document-upload'
import { MessageBarDocuments } from '../chat/ChatMessageBar/MessageBarDocuments/MessageBarDocuments'
import { useUploadDocument } from '../../hooks/useUploadDocument'
import { useUploadMedia } from '../../hooks/useUploadMedia'

const ticketChatMessageSchema = yup
  .object({
    message: yup.string().trim(),
    mediaIds: yup.array().of(yup.string().trim().required()),
    documentIds: yup.array().of(yup.string().trim().required()),
  })
  .test(
    'message-or-media-or-docs',
    'Provide a message, mediaIds, or documentIds.',
    (value) => {
      if (!value) return false
      const hasMessage = Boolean(value.message && value.message.trim())
      const hasMedia =
        Array.isArray(value.mediaIds) && value.mediaIds.length > 0
      const hasDocs =
        Array.isArray(value.documentIds) && value.documentIds.length > 0
      return hasMessage || hasMedia || hasDocs
    }
  )

const getDefaultValues = (): {
  message: string
  documentIds: []
  mediaIds: []
} => {
  return {
    message: '',
    documentIds: [],
    mediaIds: [],
  }
}

export const TicketChatBar = ({
  isSendingMessage,
  mutateSendMessage,
  societyId,
  disabled,
}: {
  isSendingMessage: boolean
  disabled?: boolean
  mutateSendMessage: UseMutateFunction<
    unknown,
    Error,
    InferType<typeof ticketChatMessageSchema>
  >
  societyId: string
}): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid },
    reset,
  } = useForm<InferType<typeof ticketChatMessageSchema>>({
    resolver: yupResolver(ticketChatMessageSchema),
    defaultValues: getDefaultValues(),
  })
  const {
    imageUploadDisabled,
    imageUploading,
    onImageRemove,
    uploadImage,
    onImagePaste,
    messageImagesDispatch,
    messageImagesState,
  } = useUploadMedia()
  const {
    documentUploadDisabled,
    documentUploading,
    onDocumentRemove,
    uploadDocument,
    documentsState,
    documentsDispatch,
  } = useUploadDocument({
    section: 'ticket',
    societyId,
  })

  const onSubmit = (data: InferType<typeof ticketChatMessageSchema>): void => {
    mutateSendMessage(
      { ...data },
      {
        onSuccess: () => {
          reset(getDefaultValues())
          messageImagesDispatch({ type: ImageActions.RESET })
          documentsDispatch({ type: DocumentActions.RESET })
        },
        onError: () => {
          setToastNotification(
            ToastType.DANGER,
            translate('flashMessage.somethingWentWrongError')
          )
        },
      }
    )
  }

  useEffect(() => {
    setValue(
      'mediaIds',
      messageImagesState.images.flatMap((_image) =>
        _image.id ? [_image.id] : []
      ),

      { shouldValidate: true }
    )
  }, [messageImagesState, setValue])

  useEffect(() => {
    setValue(
      'documentIds',
      documentsState.documents.flatMap((doc) => (doc.id ? [doc.id] : [])),
      { shouldValidate: true }
    )
  }, [documentsState, setValue])

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex-none bg-neutral-98 p-4 md:py-4 md:px-4"
    >
      <div className="flex flex-1 gap-1 self-end">
        <MessageBarUploadDocuments
          name="documentIds"
          control={control}
          uploadDisabled={
            documentUploading || documentUploadDisabled || !!disabled
          }
          onUpload={uploadDocument}
        />
        <MessageBarUpload
          name="mediaIds"
          control={control}
          imageUploadDisabled={
            imageUploadDisabled || imageUploading || !!disabled
          }
          onUploadImage={uploadImage}
        />
        <div className="flex w-full flex-col rounded-xl bg-neutral-92">
          <MessageBarDocuments
            documents={documentsState.documents}
            onDocumentRemove={onDocumentRemove}
          />

          <MessageBarImages
            images={messageImagesState.images}
            onImageRemove={onImageRemove}
          />
          <Controller
            control={control}
            name="message"
            render={({ field: { value, onChange, onBlur } }) => (
              <TextArea
                className="bg-neutral-92"
                roundedClassName="rounded-xl"
                placeholder="Aa"
                disabled={disabled}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                onPaste={onImagePaste}
                rows={1}
                removeBorders
                fixedHeight={false}
              />
            )}
          />
        </div>
        <div className="flex self-end">
          <Button
            type="submit"
            disabled={
              !isValid ||
              isSendingMessage ||
              imageUploading ||
              documentUploading ||
              disabled
            }
            className="h-11 w-11 border-none bg-transparent"
            size="no-padding"
            iconSize={20}
            variant={ButtonVariant.TEXT}
            iconColor="black"
            icon={IconChoices.SEND}
          />
        </div>
      </div>
    </form>
  )
}
